// ─── samplingEngine.js ───────────────────────────────────────────────────────
// Pure statistical functions — Rev. Proc. 2011-42 compliant

export function fmt$(n) {
  if (n == null || isNaN(n)) return '—';
  return '$' + Math.round(n).toLocaleString();
}

export function fmtPct(n) {
  if (n == null || isNaN(n)) return '—';
  return (n * 100).toFixed(1) + '%';
}

// ── Seeded random ─────────────────────────────────────────────────────────────
function mulberry32(seed) {
  return function () {
    /* eslint-disable no-mixed-operators */
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    /* eslint-enable no-mixed-operators */
  };
}

export function seededShuffle(arr, seed) {
  if (!arr || arr.length === 0) return [];
  const a = [...arr];
  const rand = mulberry32(seed);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── Toss stratum: projects below threshold excluded from sampling ─────────────
export function applyTossStratum(projects, { tossEnabled = false, tossPct = 2 } = {}) {
  if (!tossEnabled || !projects || projects.length === 0) {
    return { samplingPool: projects, tossed: [] };
  }
  const totalAmt = projects.reduce((s, p) => s + p.amount, 0);
  const tossThreshold = totalAmt * (tossPct / 100);
  
  // Sort by amount ascending, accumulate until we hit the threshold
  const sorted = [...projects].sort((a, b) => a.amount - b.amount);
  let accumulated = 0;
  const tossed = [];
  const samplingPool = [];
  
  for (const p of sorted) {
    if (accumulated + p.amount <= tossThreshold) {
      tossed.push(p);
      accumulated += p.amount;
    } else {
      samplingPool.push(p);
    }
  }
  
  return { samplingPool, tossed, tossedAmt: accumulated, tossedPct: accumulated / totalAmt };
}

// ── Stratification (Rev. Proc. 2011-42) ──────────────────────────────────────
// boundaries: optional array of dollar thresholds set by the slider
//             if provided, overrides automatic quantile calculation
export function stratifyPopulation(projects, {
  sizeMode = 'fixed',
  targetRP = 0.15,
  fixedN = 5,
  perStratumN = {},
  top100N = 5,
  numStrata = 5,
  boundaries = null, // custom dollar thresholds from slider
} = {}) {
  if (!projects || projects.length === 0) return { strata: [], large100: [] };

  // Certainty stratum: top N by dollar amount
  const sortedByAmt = [...projects].sort((a, b) => b.amount - a.amount);
  const large100 = sortedByAmt.slice(0, Math.min(top100N, projects.length));
  const large100Ids = new Set(large100.map(p => p._id));
  const rest = projects.filter(p => !large100Ids.has(p._id));

  const clampedStrata = Math.min(Math.max(numStrata ?? 5, 2), 8);
  const sortedRest = rest.map(p => p.amount).sort((a, b) => a - b);
  const strataLabels = ['Low', 'Low-Mid', 'Mid', 'Mid-High', 'High', 'Upper', 'Top-Mid', 'Top'];
  const strataColors = ['tag-s1', 'tag-s2', 'tag-s3', 'tag-s4', 'tag-s5', 'tag-s6', 'tag-s7', 'tag-s8'];

  // Use custom boundaries from slider if provided, otherwise auto-quantile
  let effectiveBoundaries;
  if (boundaries && boundaries.length === clampedStrata - 1) {
    effectiveBoundaries = [...boundaries].sort((a, b) => a - b);
  } else {
    effectiveBoundaries = [];
    for (let i = 1; i < clampedStrata; i++) {
      const idx = Math.floor((rest.length * i) / clampedStrata);
      effectiveBoundaries.push(sortedRest[idx] ?? 0);
    }
  }

  // Assign projects to strata
  const strataProjects = Array.from({ length: clampedStrata }, () => []);
  rest.forEach(p => {
    let placed = false;
    for (let i = 0; i < effectiveBoundaries.length; i++) {
      if (p.amount <= effectiveBoundaries[i]) {
        strataProjects[i].push(p);
        placed = true;
        break;
      }
    }
    if (!placed) strataProjects[clampedStrata - 1].push(p);
  });

  const calcN = (stratum, rp) => {
    if (stratum.length === 0) return 0;
    if (stratum.length <= 5) return stratum.length;
    const vals = stratum.map(p => p.amount);
    const mu = vals.reduce((s, v) => s + v, 0) / vals.length;
    const sd = Math.sqrt(vals.reduce((s, v) => s + (v - mu) ** 2, 0) / vals.length);
    const cvStr = mu > 0 ? sd / mu : 0.5;
    const nInf = Math.ceil((1.645 * Math.max(cvStr, 0.2) / rp) ** 2);
    const n = Math.ceil(nInf / (1 + (nInf - 1) / stratum.length));
    return Math.min(Math.max(n, 30), stratum.length);
  };

  const rawStrata = strataProjects
    .map((projects, i) => ({
      id: `S${i + 1}`,
      label: `Stratum ${i + 1} (${strataLabels[i] || i + 1})`,
      projects,
      tag: strataColors[i] || 'tag-s1',
      boundary: effectiveBoundaries[i] || null,
    }))
    .filter(s => s.projects.length > 0);

  rawStrata.forEach(st => {
    if (sizeMode === 'formula') {
      st.n = calcN(st.projects, targetRP);
    } else if (sizeMode === 'perStratum') {
      const ov = parseInt(perStratumN[st.id]);
      st.n = (!isNaN(ov) && ov > 0) ? Math.min(ov, st.projects.length) : Math.min(fixedN, st.projects.length);
    } else {
      st.n = Math.min(Math.max(fixedN, 1), st.projects.length);
    }
  });

  return { strata: rawStrata, large100, boundaries: effectiveBoundaries };
}

// ── Sample draw ───────────────────────────────────────────────────────────────
export function drawSample(strata, large100, seed) {
  const sample = [];
  large100.forEach(p => sample.push({ ...p, stratum: '100%', isLarge: true }));
  strata.forEach(st => {
    seededShuffle(st.projects, seed + st.id.charCodeAt(0))
      .slice(0, st.n)
      .forEach(p => sample.push({ ...p, stratum: st.id, isLarge: false }));
  });
  return sample;
}

// ── Projected RP (pre-scoring, assumes 100% qualification) ───────────────────
export function computeProjectedRP(strata, large100) {
  // Assume 100% qualification for all items — gives best-case precision
  let varianceTotal = 0;
  let pointEstimate = 0;

  // Large items 100% qualified — add to estimate, no variance
  pointEstimate += large100.reduce((s, p) => s + p.amount, 0);

  strata.forEach(st => {
    if (st.projects.length === 0 || st.n === 0) return;
    const ni = st.n;
    const Ni = st.projects.length;
    const vals = st.projects.map(p => p.amount);
    const xBar = vals.reduce((s, v) => s + v, 0) / vals.length;
    pointEstimate += Ni * xBar;
    const sdX = Math.sqrt(vals.reduce((s, v) => s + (v - xBar) ** 2, 0) / Math.max(vals.length - 1, 1));
    varianceTotal += (Ni * sdX * Math.sqrt((1 - ni / Ni) / ni)) ** 2;
  });

  if (pointEstimate === 0) return null;
  const se = Math.sqrt(varianceTotal);
  const totalSampled = strata.reduce((s, st) => s + st.n, 0);
  const z = totalSampled >= 100 ? 1.645 : 2.0;
  const precision = z * se;
  const relPrecision = precision / pointEstimate;
  return { relPrecision, pointEstimate, precision, se };
}

// ── Mean estimator ────────────────────────────────────────────────────────────
function computeMeanEstimator(sampledItems, strata, largeItems) {
  let pointEstimate = largeItems.reduce((s, p) => s + p.amount * (p.qualifiedPct / 100), 0);
  let varianceTotal = 0;

  strata.forEach(st => {
    const stSample = sampledItems.filter(p => p.stratum === st.id);
    if (stSample.length === 0) return;
    const ni = stSample.length;
    const Ni = st.Ni || st.projectCount || (st.projects && st.projects.length) || ni;
    const xs = stSample.map(p => p.amount * (p.qualifiedPct / 100));
    const xBar = xs.reduce((s, v) => s + v, 0) / ni;
    pointEstimate += Ni * xBar;
    const sdX = Math.sqrt(xs.reduce((s, v) => s + (v - xBar) ** 2, 0) / Math.max(ni - 1, 1));
    varianceTotal += (Ni * sdX * Math.sqrt((1 - ni / Ni) / ni)) ** 2;
  });

  return { pointEstimate, varianceTotal, estimatorName: 'Mean' };
}

// ── Difference estimator ──────────────────────────────────────────────────────
function computeDifferenceEstimator(sampledItems, strata, largeItems) {
  // d = x - y where x = audited value, y = reported value
  const largeQualified = largeItems.reduce((s, p) => s + p.amount * (p.qualifiedPct / 100), 0);
  // largeReported available if needed for future difference estimator refinement
  let totalDiffEstimate = largeQualified; // large items fully known
  let varianceTotal = 0;

  strata.forEach(st => {
    const stSample = sampledItems.filter(p => p.stratum === st.id);
    if (stSample.length === 0) return;
    const ni = stSample.length;
    const Ni = st.Ni || st.projectCount || (st.projects && st.projects.length) || ni;
    const stratumReportedTotal = st.stratumTotal || 0;

    // d_i = qualified - reported for each item
    const ds = stSample.map(p => p.amount * (p.qualifiedPct / 100) - p.amount);
    const dBar = ds.reduce((s, v) => s + v, 0) / ni;

    // Difference estimate for this stratum: Y_i + N_i * dBar
    totalDiffEstimate += stratumReportedTotal + Ni * dBar;

    const sdD = Math.sqrt(ds.reduce((s, v) => s + (v - dBar) ** 2, 0) / Math.max(ni - 1, 1));
    varianceTotal += (Ni * sdD * Math.sqrt((1 - ni / Ni) / ni)) ** 2;
  });

  return { pointEstimate: totalDiffEstimate, varianceTotal, estimatorName: 'Difference' };
}

// ── Bias condition check (Rev. Proc. 2011-42 §4.03(3)) ──────────────────────
function checkBiasConditions(sampledItems, strata) {
  const nonFullStrata = strata.filter(st => {
    const ni = sampledItems.filter(p => p.stratum === st.id).length;
    const Ni = st.Ni || st.projectCount || ni;
    return ni < Ni;
  });

  const totalN = nonFullStrata.reduce((s, st) => {
    return s + sampledItems.filter(p => p.stratum === st.id).length;
  }, 0);
  if (totalN < 100) return { eligible: false, reason: 'Total sample size < 100' };

  for (const st of nonFullStrata) {
    const ni = sampledItems.filter(p => p.stratum === st.id).length;
    if (ni < 30) return { eligible: false, reason: `Stratum ${st.id} has < 30 sample units (${ni})` };
  }

  const allSampled = sampledItems.filter(p => nonFullStrata.some(st => st.id === p.stratum));
  const ys = allSampled.map(p => p.amount);
  const xs = allSampled.map(p => p.amount * (p.qualifiedPct / 100));
  const ds = allSampled.map((p, i) => xs[i] - ys[i]);

  function cv(arr) {
    if (!arr || arr.length === 0) return 0;
    const mean = arr.reduce((s, v) => s + v, 0) / arr.length;
    if (Math.abs(mean) < 1e-9) return Infinity;
    const sd = Math.sqrt(arr.reduce((s, v) => s + (v - mean) ** 2, 0) / Math.max(arr.length - 1, 1));
    const result = sd / Math.abs(mean);
    return isFinite(result) ? result : 0;
  }

  const cvY = cv(ys);
  if (cvY > 0.15) return { eligible: false, reason: `CV of paired variable y = ${(cvY * 100).toFixed(1)}% > 15%` };

  const cvD = cv(ds);
  if (cvD > 0.15) return { eligible: false, reason: `CV of primary variable d = ${(cvD * 100).toFixed(1)}% > 15%` };

  return { eligible: true, cvY, cvD };
}

// ── Combined Ratio estimator (Rev. Proc. 2011-42 Appendix C) ────────────────
function computeRatioEstimator(sampledItems, strata, largeItems) {
  const bias = checkBiasConditions(sampledItems, strata);
  if (!bias.eligible) return { ...bias, estimatorName: 'Ratio', ineligible: true };

  const allY = sampledItems.map(p => p.amount);
  const allPositive = allY.every(v => v > 0);
  const allNegative = allY.every(v => v < 0);
  if (!allPositive && !allNegative) return { ineligible: true, estimatorName: 'Ratio', reason: 'Mixed signs in reported values' };

  let sumNx = 0, sumNy = 0;
  strata.forEach(st => {
    const stSample = sampledItems.filter(p => p.stratum === st.id);
    if (stSample.length === 0) return;
    const Ni = st.Ni || st.projectCount || stSample.length;
    const xBar = stSample.reduce((s, p) => s + p.amount * (p.qualifiedPct / 100), 0) / stSample.length;
    const yBar = stSample.reduce((s, p) => s + p.amount, 0) / stSample.length;
    sumNx += Ni * xBar;
    sumNy += Ni * yBar;
  });
  sumNx += largeItems.reduce((s, p) => s + p.amount * (p.qualifiedPct / 100), 0);
  sumNy += largeItems.reduce((s, p) => s + p.amount, 0);

  if (Math.abs(sumNy) < 1e-9) return null;
  const Rc = sumNx / sumNy;
  const pointEstimate = sumNx;

  let varianceTotal = 0;
  strata.forEach(st => {
    const stSample = sampledItems.filter(p => p.stratum === st.id);
    if (stSample.length === 0) return;
    const ni = stSample.length;
    const Ni = st.Ni || st.projectCount || ni;
    if (ni === Ni) return;

    const xs = stSample.map(p => p.amount * (p.qualifiedPct / 100));
    const ys = stSample.map(p => p.amount);
    const sumX  = xs.reduce((s, v) => s + v, 0);
    const sumY  = ys.reduce((s, v) => s + v, 0);
    const sumX2 = xs.reduce((s, v) => s + v * v, 0);
    const sumY2 = ys.reduce((s, v) => s + v * v, 0);
    const sumXY = xs.reduce((s, v, i) => s + v * ys[i], 0);

    const sRCi2 = (
      (sumX2 - sumX ** 2 / ni)
      - 2 * Rc * (sumXY - sumX * sumY / ni)
      + Rc ** 2 * (sumY2 - sumY ** 2 / ni)
    ) / Math.max(ni - 1, 1);

    varianceTotal += Ni ** 2 * (1 - ni / Ni) * Math.max(sRCi2, 0) / ni;
  });

  return { pointEstimate, varianceTotal, estimatorName: 'Ratio', Rc };
}

// ── Combined Regression estimator (Rev. Proc. 2011-42 Appendix C) ───────────
function computeRegressionEstimator(sampledItems, strata, largeItems) {
  const bias = checkBiasConditions(sampledItems, strata);
  if (!bias.eligible) return { ...bias, estimatorName: 'Regression', ineligible: true };

  const stratStats = [];
  strata.forEach(st => {
    const stSample = sampledItems.filter(p => p.stratum === st.id);
    if (stSample.length === 0) return;
    const ni = stSample.length;
    const Ni = st.Ni || st.projectCount || ni;
    const xs = stSample.map(p => p.amount * (p.qualifiedPct / 100));
    const ys = stSample.map(p => p.amount);
    const xBar = xs.reduce((s, v) => s + v, 0) / ni;
    const yBar = ys.reduce((s, v) => s + v, 0) / ni;
    const sumX2 = xs.reduce((s, v) => s + v * v, 0);
    const sumY2 = ys.reduce((s, v) => s + v * v, 0);
    const sumXY = xs.reduce((s, v, i) => s + v * ys[i], 0);
    const SiXY = (sumXY - ni * xBar * yBar) / Math.max(ni - 1, 1);
    const SiY2 = (sumY2 - ni * yBar ** 2) / Math.max(ni - 1, 1);
    const SiX2 = (sumX2 - ni * xBar ** 2) / Math.max(ni - 1, 1);
    stratStats.push({ st, ni, Ni, xBar, yBar, SiXY, SiY2, SiX2 });
  });

  let numerator = 0, denominator = 0;
  stratStats.forEach(({ Ni, ni, SiXY, SiY2 }) => {
    if (ni === Ni) return;
    const weight = Ni * (Ni - ni) / ni;
    numerator   += weight * SiXY;
    denominator += weight * SiY2;
  });
  if (Math.abs(denominator) < 1e-9) return null;
  const bC = numerator / denominator;

  const sumNxBar = stratStats.reduce((s, { Ni, xBar }) => s + Ni * xBar, 0)
    + largeItems.reduce((s, p) => s + p.amount * (p.qualifiedPct / 100), 0);
  const sumNyBar = stratStats.reduce((s, { Ni, yBar }) => s + Ni * yBar, 0)
    + largeItems.reduce((s, p) => s + p.amount, 0);
  const Yknown = strata.reduce((s, st) => s + (st.stratumTotal || 0), 0)
    + largeItems.reduce((s, p) => s + p.amount, 0);
  const pointEstimate = sumNxBar + bC * (Yknown - sumNyBar);

  let varianceTotal = 0;
  stratStats.forEach(({ Ni, ni, SiX2, SiXY, SiY2 }) => {
    if (ni === Ni) return;
    const sGCi2 = Math.max(SiX2 - 2 * bC * SiXY + bC ** 2 * SiY2, 0);
    varianceTotal += Ni ** 2 * (1 - ni / Ni) * sGCi2 / ni;
  });

  return { pointEstimate, varianceTotal, estimatorName: 'Regression', bC };
}

// ── Combined results computation (all four estimators) ───────────────────────
export function computeResults(scoredSample, strata, large100, population) {
  const N = population.length;
  const largeItems   = scoredSample.filter(p => p.isLarge  && p.qualifiedPct != null);
  const sampledItems = scoredSample.filter(p => !p.isLarge && p.qualifiedPct != null);
  if (sampledItems.length === 0) return null;

  // Enrich strata with reported totals for difference/regression estimators.
  // Preserve the stored stratumTotal (saved at draw time) — don't recompute from
  // st.projects because projects are emptied at draw time to save storage.
  const enrichedStrata = strata.map(st => ({
    ...st,
    stratumTotal: (st.stratumTotal != null && st.stratumTotal > 0)
      ? st.stratumTotal
      : (st.projects || []).reduce((s, p) => s + p.amount, 0),
  }));

  const meanResult       = computeMeanEstimator(sampledItems, enrichedStrata, largeItems);
  const diffResult       = computeDifferenceEstimator(sampledItems, enrichedStrata, largeItems);
  const ratioResult      = computeRatioEstimator(sampledItems, enrichedStrata, largeItems);
  const regressionResult = computeRegressionEstimator(sampledItems, enrichedStrata, largeItems);

  // Collect ineligibility reasons for UI display
  const ineligibleReasons = {};
  [ratioResult, regressionResult].forEach(r => {
    if (r && r.ineligible) ineligibleReasons[r.estimatorName] = r.reason;
  });

  const buildEstimatorOutput = (result) => {
    const { pointEstimate, varianceTotal, estimatorName } = result;
    const se = Math.sqrt(varianceTotal);
    const z  = sampledItems.length >= 100 ? 1.645 : 2.0;
    const precision = z * se;
    const lowerLimit = pointEstimate - precision;
    const relPrecision = pointEstimate > 0 ? precision / pointEstimate : null;

    let reportedEstimate, estimateMethod;
    if (relPrecision !== null && relPrecision <= 0.10) {
      reportedEstimate = pointEstimate;
      estimateMethod = 'Point Estimate (RP ≤ 10%)';
    } else if (relPrecision !== null && relPrecision <= 0.15) {
      reportedEstimate = pointEstimate - ((relPrecision - 0.10) / 0.05) * (pointEstimate - lowerLimit);
      estimateMethod = 'Interpolated (10% < RP ≤ 15%)';
    } else {
      reportedEstimate = lowerLimit;
      estimateMethod = 'Least Advantageous 95% Lower Limit (RP > 15%)';
    }

    return { pointEstimate, se, precision, lowerLimit, upperLimit: pointEstimate + precision,
             relPrecision, reportedEstimate, estimateMethod, z, estimatorName };
  };

  const meanOutput       = buildEstimatorOutput(meanResult);
  const diffOutput       = buildEstimatorOutput(diffResult);
  const ratioOutput      = ratioResult && !ratioResult.ineligible ? buildEstimatorOutput(ratioResult) : null;
  const regressionOutput = regressionResult && !regressionResult.ineligible ? buildEstimatorOutput(regressionResult) : null;

  // Build candidate list — only eligible estimators per §4.03(1)
  const candidates = [meanOutput, diffOutput, ratioOutput, regressionOutput].filter(Boolean);

  // Select estimator with smallest absolute standard error per §4.03(1)
  const bestOutput = candidates.reduce((a, b) => a.se <= b.se ? a : b);
  const recommended = bestOutput.estimatorName;

  // Attribute stats
  const propSampled = sampledItems.filter(p => p.qualifiedPct > 0).length / sampledItems.length;
  const seP = Math.sqrt((propSampled * (1 - propSampled)) / sampledItems.length);
  const z = sampledItems.length >= 100 ? 1.645 : 2.0;

  const totalSampleAmt = scoredSample.filter(p => p.qualifiedPct != null).reduce((s, p) => s + p.amount, 0);
  const totalQualified = scoredSample.filter(p => p.qualifiedPct != null).reduce((s, p) => s + p.amount * (p.qualifiedPct / 100), 0);

  return {
    mean: meanOutput,
    difference: diffOutput,
    ratio: ratioOutput,
    regression: regressionOutput,
    ineligibleReasons,
    recommended,
    propQualifying: propSampled,
    propLower: Math.max(0, propSampled - z * seP),
    propUpper: Math.min(1, propSampled + z * seP),
    avgQualRate: totalSampleAmt > 0 ? totalQualified / totalSampleAmt : 0,
    totalPop: population.reduce((s, p) => s + p.amount, 0),
    N,
    nSampled: sampledItems.length,
    smallSampleWarning: sampledItems.length < 30,
  };
}


// ── Attribute sampling (Rev. Proc. 2011-42 §4.05) ────────────────────────────
// Hypergeometric-based sample size for attribute sampling
// confidenceLevel: 0.90 or 0.95
// tolerableRate: proportion (e.g. 0.05 = 5%)
// expectedRate: proportion (e.g. 0.02 = 2%)
export function computeAttributeSampleSize(N, { confidenceLevel = 0.95, tolerableRate = 0.05, expectedRate = 0.00 } = {}) {
  if (N <= 0) return null;

  // z-score for one-sided confidence
  const z = confidenceLevel >= 0.95 ? 1.645 : 1.282;

  // Infinite population formula: n0 = z² * p(1-p) / e²
  // where p = expected rate, e = tolerable - expected (precision gap)
  const p = Math.max(expectedRate, 0.0001);
  const e = tolerableRate - expectedRate;
  if (e <= 0) return null; // tolerable must exceed expected

  const n0 = Math.ceil((z * z * p * (1 - p)) / (e * e));

  // Finite population correction
  const n = Math.ceil(n0 / (1 + (n0 - 1) / N));

  // Minimum 1, maximum N
  const finalN = Math.min(Math.max(n, 1), N);

  return {
    n: finalN,
    n0,
    z,
    confidenceLevel,
    tolerableRate,
    expectedRate,
    N,
    fpCorrection: n < n0,
  };
}

// Attribute results evaluation per Rev. Proc. 2011-42 §4.05
export function evaluateAttributeResults(sampleSize, errorsFound, N, { confidenceLevel = 0.95, tolerableRate = 0.05 } = {}) {
  if (sampleSize <= 0) return null;

  const z = confidenceLevel >= 0.95 ? 1.645 : 1.282;
  const observedRate = errorsFound / sampleSize;

  // Upper confidence limit on error rate (Wilson score interval, one-sided)
  const n = sampleSize;
  const p = observedRate;
  const denominator = 1 + z * z / n;
  const centre = p + z * z / (2 * n);
  const margin = z * Math.sqrt(p * (1 - p) / n + z * z / (4 * n * n));
  const upperErrorLimit = (centre + margin) / denominator;

  // Population projection
  const projectedErrors = upperErrorLimit * N;
  const passesTest = upperErrorLimit <= tolerableRate;

  return {
    observedRate,
    upperErrorLimit,
    projectedErrors,
    passesTest,
    tolerableRate,
    confidenceLevel,
    errorsFound,
    sampleSize,
    conclusion: passesTest
      ? `PASS — Upper error limit (${fmtPct(upperErrorLimit)}) does not exceed tolerable rate (${fmtPct(tolerableRate)}).`
      : `FAIL — Upper error limit (${fmtPct(upperErrorLimit)}) exceeds tolerable rate (${fmtPct(tolerableRate)}). Population does not support the attribute tested.`,
  };
}

// Recommendation: suggest attribute when population < 500 or variable n < 30
export function getSamplingModeRecommendation(populationSize, variableSampleN) {
  if (populationSize < 100) {
    return {
      recommended: 'attribute',
      reason: `Population of ${populationSize} projects is small. Attribute sampling is generally more efficient and appropriate for populations under 100.`,
    };
  }
  if (populationSize < 500 && variableSampleN < 30) {
    return {
      recommended: 'attribute',
      reason: `Population of ${populationSize} with a variable sample of ${variableSampleN} is below the 30-project minimum. Attribute sampling is recommended.`,
    };
  }
  if (populationSize < 500) {
    return {
      recommended: 'consider_attribute',
      reason: `Population of ${populationSize} is below 500. Consider whether attribute sampling may be more efficient for this engagement.`,
    };
  }
  if (variableSampleN < 30) {
    return {
      recommended: 'attribute',
      reason: `Variable sample size of ${variableSampleN} is below the 30-project minimum recommended for valid statistical inference. Attribute sampling is recommended, or increase strata/n per stratum.`,
    };
  }
  return { recommended: 'variable', reason: null };
}

// ── Per-tax-year QRE breakdown ────────────────────────────────────────────────
export function computeYearBreakdown(population, scoredSample, reportedEstimate) {
  if (!population || !reportedEstimate) return [];
  const totalPop = population.reduce((s, p) => s + p.amount, 0);
  if (totalPop === 0) return [];

  const yearTotals = {};
  population.forEach(p => {
    const yr = p.year ? String(p.year) : 'Unknown';
    yearTotals[yr] = (yearTotals[yr] || 0) + p.amount;
  });

  return Object.entries(yearTotals)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([year, amt]) => ({
      year,
      populationAmt: amt,
      apportionment: amt / totalPop,
      estimatedQRE: (amt / totalPop) * reportedEstimate,
    }));
}
